import { compactOrganizationLayout } from "./organization-layout";
import { alpha, useTheme } from "@mui/material/styles";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  TextField,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ChevronDown,
  ChevronRight,
  Crown,
  Maximize,
  Minus,
  Plus,
  Users,
} from "lucide-react";
import { profileImageUrl } from "@web/utils/upload-url";
import type { Person } from "./OrganizationPage";

export function departmentColor(
  person: Pick<Person, "department" | "levelId">,
  mode: "light" | "dark" = "dark",
) {
  const light = mode === "light";
  if (person.levelId === 1) return light ? "#8a5600" : "#edc879";
  // After executive management, color denotes organizational level.
  if (person.levelId === 3) return light ? "#7c3caf" : "#c084fc";
  if (person.levelId === 4) return light ? "#066e67" : "#2dd4bf";
  if (person.levelId === 5) return light ? "#a04407" : "#fb923c";
  const department = person.department?.toLowerCase() || "";
  if (department.includes("sales")) return light ? "#b71946" : "#fb7185";
  if (department === "it" || department.includes("information"))
    return light ? "#036b9e" : "#38bdf8";
  if (department.startsWith("hr") || department.includes("human"))
    return light ? "#05704c" : "#34d399";
  if (department.includes("account") || department.includes("finance"))
    return light ? "#7c3caf" : "#c084fc";
  if (department.includes("media")) return light ? "#895500" : "#fbbf24";
  if (department.includes("operation")) return light ? "#066e67" : "#2dd4bf";
  return light ? "#475569" : "#94a3b8";
}
export function overviewCollapsedIds(people: Person[]) {
  const ids = new Set(people.map((p) => p.id));
  return new Set(
    people
      .filter(
        (p) =>
          p.managerId !== null &&
          ids.has(p.managerId) &&
          people.some((child) => child.managerId === p.id),
      )
      .map((p) => p.id),
  );
}
export function readableChartScale(
  viewportWidth: number,
  contentWidth: number,
) {
  return contentWidth > 0
    ? Math.max(0.01, Math.min(1, (viewportWidth - 48) / contentWidth))
    : 1;
}
export function reportingBranchWidth(
  person: Person,
  reports: Map<number, Person[]>,
  collapsed: Set<number>,
  ancestors: number[] = [],
): number {
  const ownWidth = (person.levelId === 1 ? 288 : 240) + 24;
  if (collapsed.has(person.id) || ancestors.includes(person.id))
    return ownWidth;
  const widths = (reports.get(person.id) || []).map((child) =>
    reportingBranchWidth(child, reports, collapsed, [...ancestors, person.id]),
  );
  return Math.max(
    ownWidth,
    24 + widths.reduce((total, width) => total + width, 0),
  );
}
export function OrganizationChart({
  people,
  onEdit,
  title = "Organization structure",
  defaultExpanded = false,
  directorStaffOnRight = false,
}: {
  people: Person[];
  onEdit?: (person: Person) => void;
  title?: string;
  defaultExpanded?: boolean;
  directorStaffOnRight?: boolean;
}) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const accent = departmentColor(
    { levelId: 1, department: undefined },
    theme.palette.mode,
  );
  const [staffSearch, setStaffSearch] = useState("");
  const [staffDepartment, setStaffDepartment] = useState("");
  const [staffLevel, setStaffLevel] = useState("");
  const [collapsed, setCollapsed] = useState<Set<number>>(() =>
    defaultExpanded ? new Set<number>() : overviewCollapsedIds(people),
  );
  const [scale, setScale] = useState(1);
  const [cardHeight, setCardHeight] = useState(200);
  const [cardHeights, setCardHeights] = useState<Record<number, number>>({});
  const [size, setSize] = useState({ width: 0, height: 0 });
  const focusPerson = useRef<number | null>(null);
  const viewport = useRef<HTMLDivElement>(null),
    content = useRef<HTMLDivElement>(null);
  const ids = new Set(people.map((p) => p.id));
  const reports = new Map<number, Person[]>();
  for (const person of people)
    if (person.managerId && ids.has(person.managerId))
      reports.set(person.managerId, [
        ...(reports.get(person.managerId) || []),
        person,
      ]);
  const sort = (rows: Person[]) =>
    [...rows].sort(
      (a, b) =>
        (a.levelId ?? 99) - (b.levelId ?? 99) || a.name.localeCompare(b.name),
    );
  const roots = sort(
    people.filter((p) => !p.managerId || !ids.has(p.managerId)),
  );
  const leaders = roots.filter((p) => p.levelId === 1 || reports.has(p.id));
  for (const [id, rows] of reports) reports.set(id, sort(rows));
  const sideReportIds = new Set<number>();
  if (directorStaffOnRight) {
    for (const director of people.filter(
      (person) => person.designation?.trim().toLowerCase() === "sales director",
    )) {
      for (const report of reports.get(director.id) || []) {
        const title = report.designation?.toLowerCase() || "";
        if (
          title.includes("consultant") ||
          ((report.levelId ?? 0) >= 4 && !title.includes("manager"))
        )
          sideReportIds.add(report.id);
      }
    }
  }
  const layout = compactOrganizationLayout(
    leaders,
    reports,
    collapsed,
    sideReportIds,
  );
  const positions = new Map(layout.nodes.map((node) => [node.id, node]));
  const rowHeight = cardHeight + 40;
  const cardTops = new Map<number, number>();
  function cardTop(id: number): number {
    if (cardTops.has(id)) return cardTops.get(id)!;
    const position = positions.get(id)!;
    const person = people.find((person) => person.id === id)!;
    const parent = person.managerId
      ? positions.get(person.managerId)
      : undefined;
    const top = parent
      ? cardTop(parent.id) +
        (position.depth - parent.depth) * rowHeight +
        (sideReportIds.has(id)
          ? ((cardHeights[parent.id] || cardHeight) -
              (cardHeights[id] || cardHeight)) /
            2
          : 0)
      : position.depth * rowHeight;
    cardTops.set(id, top);
    return top;
  }
  const disconnected = roots.filter((p) => !leaders.includes(p));
  const staffDepartments = [
    ...new Set(
      disconnected
        .map((p) => p.department)
        .filter((d): d is string => Boolean(d)),
    ),
  ].sort();
  const visibleDisconnected = disconnected.filter(
    (person) =>
      (!staffSearch ||
        `${person.name} ${person.designation || ""} ${person.department || ""}`
          .toLowerCase()
          .includes(staffSearch.toLowerCase())) &&
      (!staffDepartment || person.department === staffDepartment) &&
      (!staffLevel ||
        (staffLevel === "unset"
          ? person.levelId === null
          : person.levelId === Number(staffLevel))),
  );
  const fit = useCallback(() => {
    const width = content.current?.offsetWidth || 0;
    if (width && viewport.current) {
      const nextScale = readableChartScale(viewport.current.clientWidth, width);
      setScale(nextScale);
      requestAnimationFrame(() => {
        const board = viewport.current;
        const card = focusPerson.current
          ? content.current?.querySelector<HTMLElement>(
              `[data-person-id="${focusPerson.current}"]`,
            )
          : null;
        if (board && !card) board.scrollLeft = 0;
        if (board && card) {
          const bounds = board.getBoundingClientRect(),
            target = card.getBoundingClientRect();
          board.scrollLeft +=
            target.left - bounds.left - (board.clientWidth - target.width) / 2;
        }
      });
    }
  }, []);
  useLayoutEffect(() => {
    let previousWidth = -1,
      previousHeight = -1,
      previousViewport = -1;
    function measure() {
      if (!content.current || !viewport.current) return;
      const width = content.current.offsetWidth,
        height = content.current.offsetHeight,
        available = viewport.current.clientWidth;
      const heights = Object.fromEntries(
        Array.from(
          content.current.querySelectorAll<HTMLElement>("[data-person-id]"),
        ).map((card) => [Number(card.dataset.personId), card.offsetHeight]),
      );
      setCardHeights((previous) =>
        JSON.stringify(previous) === JSON.stringify(heights)
          ? previous
          : heights,
      );
      if (
        width === previousWidth &&
        height === previousHeight &&
        available === previousViewport
      )
        return;
      previousWidth = width;
      previousHeight = height;
      previousViewport = available;
      const measuredHeight = Math.max(
        128,
        ...Array.from(
          content.current.querySelectorAll<HTMLElement>("[data-person-id]"),
        ).map((card) => card.offsetHeight),
      );
      if (measuredHeight > 128) setCardHeight(measuredHeight);
      setSize({ width, height });
      fit();
    }
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    if (viewport.current) observer.observe(viewport.current);
    if (content.current) {
      observer.observe(content.current);
      content.current
        .querySelectorAll("[data-person-id]")
        .forEach((card) => observer.observe(card));
    }
    return () => observer.disconnect();
  }, [people, collapsed, fit, cardHeight]);
  function toggle(id: number) {
    focusPerson.current = id;
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function card(person: Person, branch = false) {
    const color = departmentColor(person, theme.palette.mode);
    const children = reports.get(person.id) || [];
    const open = !collapsed.has(person.id);
    return (
      <Paper
        variant="outlined"
        data-person-id={person.id}
        sx={{
          width: person.levelId === 1 ? 288 : 240,
          minHeight: 128,
          p: 1.5,
          textAlign: "left",
          position: "relative",
          borderColor: color,
          color: theme.palette.text.primary,
          borderRadius: 2.5,
          background: dark
            ? `linear-gradient(135deg,${color}16,rgba(10,19,33,.97) 65%)`
            : `linear-gradient(135deg,${alpha(color, 0.06)},${theme.palette.background.paper} 65%)`,
          boxShadow: `inset 0 0 18px ${color}12, 0 0 16px ${color}24`,
          transition: "box-shadow .15s ease,border-color .15s ease",
          "&:hover": {
            boxShadow: `inset 0 0 18px ${color}22,0 0 22px ${color}44`,
          },
          borderTop: `1px solid ${color}`,
          borderLeft: `3px solid ${color}`,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography
            variant="overline"
            sx={{ color, fontSize: 10, letterSpacing: 1.3, lineHeight: 1.3 }}
          >
            {person.department || "Department not set"}
          </Typography>
          <Chip
            size="small"
            icon={person.levelId === 1 ? <Crown size={13} /> : undefined}
            label={person.levelId ? `Level ${person.levelId}` : "Level not set"}
            sx={{
              height: 23,
              fontSize: 10,
              color,
              border: `1px solid ${color}66`,
              bgcolor: `${color}12`,
              "& .MuiChip-icon": { color },
            }}
          />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            src={profileImageUrl(person.profileImageUrl)}
            alt={person.name}
            sx={{
              width: 56,
              height: 56,
              border: `2px solid ${color}88`,
              bgcolor: dark ? "#243449" : alpha(color, 0.1),
              color: dark ? "#fff" : color,
            }}
          >
            {person.name
              .trim()
              .split(/\s+/)
              .slice(0, 2)
              .map((n) => n[0])
              .join("")}
          </Avatar>
          <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
            <Typography
              variant="body2"
              sx={{ color, fontWeight: 700, lineHeight: 1.35, mb: 0.4 }}
            >
              {person.designation || "Designation not set"}
            </Typography>
            <Typography fontWeight={600} sx={{ fontSize: 17 }}>
              {person.name}
            </Typography>
          </Box>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 1.5, minHeight: 28 }}
        >
          {branch && children.length > 0 ? (
            <Button
              size="small"
              aria-expanded={open}
              aria-label={`${open ? "Collapse" : "Expand"} ${person.name}'s team`}
              onClick={() => toggle(person.id)}
              startIcon={
                open ? <ChevronDown size={14} /> : <ChevronRight size={14} />
              }
              sx={{ color: theme.palette.text.secondary, fontSize: 11, p: 0 }}
            >
              {children.length} direct report{children.length === 1 ? "" : "s"}
            </Button>
          ) : (
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary }}
            >
              {person.status === "ACTIVE" ? "Staff member" : person.status}
            </Typography>
          )}
          {onEdit && (
            <Button
              size="small"
              aria-label="Edit reporting"
              onClick={() => onEdit(person)}
              sx={{ fontSize: 11, color }}
            >
              Edit reporting
            </Button>
          )}
        </Stack>
      </Paper>
    );
  }
  function node(person: Person, ancestors: number[] = []) {
    const position = positions.get(person.id);
    if (!position || ancestors.includes(person.id)) return null;
    const parent = ancestors.length
      ? positions.get(ancestors[ancestors.length - 1])
      : undefined;
    const children = reports.get(person.id) || [];
    return (
      <Box
        component="li"
        key={person.id}
        sx={{
          position: "absolute",
          listStyle: "none",
          width: position.width,
          left: parent
            ? position.x - parent.x + parent.width / 2 - position.width / 2
            : position.x - position.width / 2,
          top: cardTop(person.id) - (parent ? cardTop(parent.id) : 0),
        }}
      >
        {card(person, true)}
        {children.length > 0 && !collapsed.has(person.id) && (
          <Box
            component="ul"
            aria-label={`${person.name}'s direct reports`}
            sx={{ m: 0, p: 0 }}
          >
            {children.map((child) => node(child, [...ancestors, person.id]))}
          </Box>
        )}
      </Box>
    );
  }
  return (
    <Stack spacing={1.25} aria-label="Organization chart">
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
        <Users size={18} />
        <Typography sx={{ flex: 1 }}>
          {people.length} staff ·{" "}
          {new Set(people.map((p) => p.department).filter(Boolean)).size}{" "}
          departments
        </Typography>
        <Button size="small" onClick={() => setCollapsed(new Set())}>
          Expand all
        </Button>
        <Button
          size="small"
          onClick={() => setCollapsed(new Set(reports.keys()))}
        >
          Collapse all
        </Button>
        <Tooltip title="Zoom out">
          <span>
            <IconButton
              aria-label="Zoom out"
              size="small"
              disabled={scale <= 0.1}
              onClick={() => setScale((s) => Math.max(0.1, s - 0.1))}
            >
              <Minus size={17} />
            </IconButton>
          </span>
        </Tooltip>
        <Typography
          variant="caption"
          sx={{ minWidth: 35, textAlign: "center" }}
        >
          {Math.round(scale * 100)}%
        </Typography>
        <Tooltip title="Zoom in">
          <span>
            <IconButton
              aria-label="Zoom in"
              size="small"
              disabled={scale >= 1.5}
              onClick={() => setScale((s) => Math.min(1.5, s + 0.1))}
            >
              <Plus size={17} />
            </IconButton>
          </span>
        </Tooltip>
        <Button size="small" startIcon={<Maximize size={14} />} onClick={fit}>
          Fit view
        </Button>
      </Stack>
      <Box
        sx={{
          border: "1px solid",
          borderColor: alpha(accent, 0.6),
          boxShadow: `inset 2px 0 0 ${accent},0 0 28px ${alpha(accent, 0.07)}`,
          borderRadius: 3,
          overflow: "hidden",
          background: dark
            ? "radial-gradient(ellipse at 50% 0%,rgba(12,29,48,.86) 0%,rgba(3,15,29,.78) 65%), url(/branding/orbit-glass-dubai.jpg)"
            : `linear-gradient(135deg,${theme.palette.background.paper},${alpha(theme.palette.primary.main, 0.045)})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            p: 1.25,
            borderBottom: `1px solid ${alpha(accent, 0.2)}`,
            color: accent,
          }}
        >
          <Crown size={18} />
          <Typography variant="overline" letterSpacing={2}>
            {title}
          </Typography>
        </Stack>
        {leaders.length > 0 ? (
          <Box
            ref={viewport}
            sx={{
              overflow: "auto",
              height: "62vh",
              minHeight: 300,
              maxHeight: 760,
              minWidth: 0,
            }}
            aria-label="Connected reporting chart"
          >
            <Box
              sx={{
                position: "relative",
                width: Math.max(0, size.width * scale + 48),
                minWidth: "100%",
                height: Math.max(300, size.height * scale + 48),
              }}
            >
              <Box
                ref={content}
                sx={{
                  position: "absolute",
                  left: 24,
                  top: 24,
                  width: layout.width,
                  height: layout.depth * rowHeight - 40,
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                }}
              >
                <svg
                  aria-hidden="true"
                  width={layout.width}
                  height={layout.depth * rowHeight}
                  style={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    overflow: "visible",
                  }}
                >
                  {layout.nodes.map((child) => {
                    const person = people.find(
                      (person) => person.id === child.id,
                    )!;
                    const parent = person.managerId
                      ? positions.get(person.managerId)
                      : undefined;
                    if (!parent) return null;
                    const start =
                      cardTop(parent.id) +
                      (cardHeights[parent.id] || cardHeight);
                    const end = cardTop(child.id);
                    const middle = end - 20;
                    const sideBranch = sideReportIds.has(child.id);
                    const startX = parent.x + parent.width / 2;
                    const startY =
                      cardTop(parent.id) +
                      (cardHeights[parent.id] || cardHeight) / 2;
                    const endX = child.x - child.width / 2;
                    const endY =
                      end + (cardHeights[child.id] || cardHeight) / 2;
                    const path = sideBranch
                      ? child.depth === parent.depth
                        ? `M ${startX} ${startY} H ${endX}`
                        : `M ${startX} ${startY} H ${endX - 36} V ${endY} H ${endX}`
                      : `M ${parent.x} ${start} V ${middle} H ${child.x} V ${end}`;
                    return (
                      <path
                        key={child.id}
                        d={path}
                        fill="none"
                        stroke={departmentColor(person, theme.palette.mode)}
                        strokeWidth={2}
                      />
                    );
                  })}
                </svg>
                <Box
                  component="ul"
                  aria-label="Leadership reporting branches"
                  sx={{ m: 0, p: 0, position: "relative", flexWrap: "nowrap" }}
                >
                  {leaders.map((person) => node(person))}
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          <Typography sx={{ p: 4, color: theme.palette.text.secondary }}>
            No connected reporting structure yet. Staff are listed below until a
            reporting manager is assigned.
          </Typography>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary">
        Child teams start collapsed. Expand a person to view their designated
        staff; Fit view shows the complete chart width. Zoom in for more detail.{" "}
        {directorStaffOnRight
          ? "Managers appear below the Sales Director; staff reporting directly to the director appear on the right. "
          : "Leadership stays on the same row. "}
        Zoom changes only the chart content; wide teams can be scrolled inside
        the panel.
      </Typography>
      {disconnected.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Staff without a reporting manager ({disconnected.length})
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
            These staff remain visible until their reporting line is configured.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} gap={1} sx={{ mb: 2 }}>
            <TextField
              size="small"
              label="Search staff"
              value={staffSearch}
              onChange={(e) => setStaffSearch(e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              select
              size="small"
              label="Department"
              value={staffDepartment}
              onChange={(e) => setStaffDepartment(e.target.value)}
              sx={{ minWidth: 170 }}
            >
              <MenuItem value="">All departments</MenuItem>
              {staffDepartments.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Level"
              value={staffLevel}
              onChange={(e) => setStaffLevel(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All levels</MenuItem>
              {[
                ...new Set(
                  people
                    .map((p) => p.levelId)
                    .filter((l): l is number => l !== null),
                ),
              ]
                .sort((a, b) => a - b)
                .map((l) => (
                  <MenuItem key={l} value={String(l)}>
                    Level {l}
                  </MenuItem>
                ))}
              <MenuItem value="unset">Level not set</MenuItem>
            </TextField>
          </Stack>
          {visibleDisconnected.length === 0 && (
            <Typography role="status" color="text.secondary">
              No staff match these filters.
            </Typography>
          )}
          <Box
            component="ul"
            aria-label="Staff without a reporting manager"
            sx={{
              m: 0,
              p: 0,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(210px,1fr))",
              gap: 2,
              "& > li > .MuiPaper-root": { width: "100%" },
            }}
          >
            {visibleDisconnected.map((person) => (
              <Box component="li" key={person.id} sx={{ listStyle: "none" }}>
                {card(person)}
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {people.length === 0 && (
        <Typography color="text.secondary">No staff available.</Typography>
      )}
    </Stack>
  );
}
